/**
 * 工作区成员操作相关的自定义Hooks
 * 封装成员管理的业务逻辑，提高代码复用性和可维护性
 */

import { useCallback, useState } from 'react'
import {
  useRemoveMember,
  useInviteMsg,
  useAcceptInvite,
  useGenerateInviteLinkMutation,
} from '@/api/hooks/useWorkspace'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import type { WorkspaceMemberVO } from '@/api/types/workspace.types'

/**
 * 成员移除操作Hook
 * @param onSuccess 成功回调
 * @param onError 错误回调
 */
export const useRemoveMemberAction = (
  onSuccess?: () => void,
  onError?: (error: Error) => void
) => {
  const { currentWorkspace } = useWorkspaceStore()
  const { trigger: removeMember, isMutating, error } = useRemoveMember()

  const handleRemoveMember = useCallback(
    async (member: WorkspaceMemberVO) => {
      // 权限检查
      if (member.isOwner === 1) {
        throw new Error('无法移除工作区拥有者')
      }
      if (member.isCurrentUser === 1) {
        throw new Error('无法移除自己')
      }
      if (!member.userId) {
        throw new Error('用户信息不完整')
      }
      if (!currentWorkspace?.id) {
        throw new Error('未选择工作区')
      }

      try {
        await removeMember({
          userId: member.userId,
          workspaceId: currentWorkspace.id,
        })
        onSuccess?.()
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error : new Error('移除成员失败')
        onError?.(errorMessage)
        throw errorMessage
      }
    },
    [removeMember, currentWorkspace?.id, onSuccess, onError]
  )

  return {
    removeMember: handleRemoveMember,
    isRemoving: isMutating,
    error,
  }
}

/**
 * 邀请链接生成操作Hook
 * @param onSuccess 成功回调
 * @param onError 错误回调
 */
export const useInviteLinkAction = (
  onSuccess?: (url: string) => void,
  onError?: (error: Error) => void
) => {
  const { currentWorkspace } = useWorkspaceStore()
  const { trigger: generateInviteLink, isMutating } =
    useGenerateInviteLinkMutation()

  // 处理邀请链接格式
  const processInviteUrl = useCallback((apiUrl: string): string => {
    console.log('开始处理邀请链接:', apiUrl)

    try {
      // 解析API返回的URL
      const url = new URL(apiUrl)
      const code = url.searchParams.get('code')

      if (!code) {
        console.warn('无法从邀请链接中提取code参数:', apiUrl)
        return apiUrl // 如果提取失败，返回原链接
      }

      // 使用当前页面的host构建新的邀请链接
      const currentHost = window.location.origin
      const newInviteUrl = `${currentHost}/workspace/invite?code=${code}`

      console.log('邀请链接处理完成:', {
        原链接: apiUrl,
        提取的code: code,
        当前host: currentHost,
        新链接: newInviteUrl,
      })

      return newInviteUrl
    } catch (error) {
      console.error('处理邀请链接时出错:', error, '原链接:', apiUrl)
      return apiUrl // 如果处理失败，返回原链接
    }
  }, [])

  // 手动生成邀请链接
  const handleGenerateInviteLink = useCallback(async () => {
    if (!currentWorkspace?.id) {
      const error = new Error('未选择工作区')
      onError?.(error)
      throw error
    }

    try {
      const result = await generateInviteLink({
        workspaceId: currentWorkspace.id,
      })
      if (result?.invitationUrl) {
        const processedUrl = processInviteUrl(result.invitationUrl)
        onSuccess?.(processedUrl)
      }
      return result
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error : new Error('生成邀请链接失败')
      onError?.(errorMessage)
      throw errorMessage
    }
  }, [
    currentWorkspace?.id,
    generateInviteLink,
    processInviteUrl,
    onSuccess,
    onError,
  ])

  return {
    generateInviteLink: handleGenerateInviteLink,
    isGenerating: isMutating,
  }
}

/**Add commentMore actions
 * 成员权限检查工具函数
 */
export const memberPermissions = {
  /**
   * 检查是否可以移除成员
   */
  canRemoveMember: (member: WorkspaceMemberVO): boolean => {
    return member.isOwner !== 1 && member.isCurrentUser !== 1
  },

  /**
   * 检查是否为工作区拥有者
   */
  isOwner: (member: WorkspaceMemberVO): boolean => {
    return member.isOwner === 1
  },

  /**
   * 检查是否为当前用户
   */
  isCurrentUser: (member: WorkspaceMemberVO): boolean => {
    return member.isCurrentUser === 1
  },

  /**
   * 获取成员角色显示文本
   */
  getRoleText: (member: WorkspaceMemberVO): string => {
    if (member.isOwner === 1) return '所有者'
    return '成员'
  },
}

/**
 * 剪贴板操作Hook
 */
export const useClipboard = () => {
  const copyToClipboard = useCallback(
    async (text: string): Promise<boolean> => {
      try {
        await navigator.clipboard.writeText(text)
        return true
      } catch (error) {
        console.error('复制到剪贴板失败:', error)
        // 兜底方案：使用传统方法
        try {
          const textArea = document.createElement('textarea')
          textArea.value = text
          document.body.appendChild(textArea)
          textArea.focus()
          textArea.select()
          const result = document.execCommand('copy')
          document.body.removeChild(textArea)
          return result
        } catch (fallbackError) {
          console.error('兜底复制方法也失败:', fallbackError)
          return false
        }
      }
    },
    []
  )

  return { copyToClipboard }
}

/**
 * 工作区邀请操作Hook
 * 封装邀请页面相关的业务逻辑
 */
export const useWorkspaceInvite = (code?: string) => {
  const [isAccepted, setIsAccepted] = useState(false)

  // 获取邀请信息
  const {
    data: inviteData,
    error: inviteError,
    isLoading: isLoadingInvite,
    mutate: refetchInvite,
  } = useInviteMsg(code)

  // 接受邀请
  const {
    trigger: acceptInvite,
    isMutating: isAccepting,
    error: acceptError,
  } = useAcceptInvite()

  // 处理接受邀请
  const handleAcceptInvite = useCallback(async () => {
    if (!code) return

    try {
      await acceptInvite({ code })
      setIsAccepted(true)
      return true
    } catch (error) {
      console.error('接受邀请失败:', error)
      return false
    }
  }, [code, acceptInvite])

  // 重置状态
  const resetState = useCallback(() => {
    setIsAccepted(false)
  }, [])

  return {
    // 数据
    inviteData,
    isAccepted,

    // 状态
    isLoadingInvite,
    isAccepting,

    // 错误
    inviteError,
    acceptError,

    // 操作
    handleAcceptInvite,
    refetchInvite,
    resetState,
  }
}
